import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import type { Category } from '@ankor-io/common/types/transaction/category'
import { TransactionStatus, TransactionType } from '@ankor-io/common/types/transaction/transaction'
import { getStatusText } from '@ankor-io/common/util/enum-to-string/transaction'
import { getTransactionTypeText } from '@ankor-io/common/util/enum-to-string/transaction.type'

import { useUserContext } from '@/providers/user/useUserContext'
import Chip from '@/ui-components/Chip'
import { Input } from '@/ui-components/Input'
import { Listbox, ListboxOption } from '@/ui-components/Listbox'
import Loading from '@/ui-components/Loading'
import { Select } from '@/ui-components/Select'

export type TransactionFilters = {
  dateFrom: string
  dateTo: string
  status: string
  categoryId: string
  type: TransactionType | ''
}

type TransactionFiltersProps = {
  transactionFilters: TransactionFilters
  setTransactionFilters: (transactionFilters: TransactionFilters) => void
}

const TransactionFilters = ({ transactionFilters, setTransactionFilters }: TransactionFiltersProps) => {
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId } = useUserContext()

  const [isLoading, setIsLoading] = useState(true)
  const [categories, setCategories] = useState<Category[]>([])

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true)
        const res = await fetch(`/api/pay/org/${activeOrganizationId}/categories`, {
          headers: {
            Authorization: `Bearer ${await getAccessToken()}`,
          },
        })

        if (res.ok) {
          const categories = await res.json()
          setCategories(categories)
        }
      } catch (error) {
        if (!(error instanceof Error && error.name === 'AbortError')) {
          console.error('Failed to fetch categories:', error)
          toast.error('Failed to fetch categories')
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchCategories()
  }, [activeOrganizationId, getAccessToken])

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-2">
      <Input
        type="date"
        placeholder="Date From"
        aria-label="Date From"
        value={transactionFilters.dateFrom}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setTransactionFilters({ ...transactionFilters, dateFrom: e.target.value })
        }
      />
      <Input
        type="date"
        placeholder="Date To"
        aria-label="Date To"
        value={transactionFilters.dateTo}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setTransactionFilters({ ...transactionFilters, dateTo: e.target.value })
        }
      />

      <Select
        value={transactionFilters.status}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          setTransactionFilters({ ...transactionFilters, status: e.target.value })
        }
      >
        <option value="">All Statuses</option>
        {Object.values(TransactionStatus).map((status) => (
          <option
            key={status}
            value={status}
          >
            {getStatusText(status)}
          </option>
        ))}
      </Select>

      <div className="md:col-span-3 xl:col-span-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-2">
        {isLoading ? (
          <div className="flex items-center justify-center">
            <Loading />
          </div>
        ) : (
          <Listbox
            value={transactionFilters.categoryId}
            onChange={(value: string) => setTransactionFilters({ ...transactionFilters, categoryId: value })}
          >
            <ListboxOption value="">Select a category</ListboxOption>
            {categories.map((category) => (
              <ListboxOption
                key={category.id}
                value={category.id}
              >
                <Chip
                  name={category.name}
                  colour={category.color}
                  icon={category.icon}
                />
              </ListboxOption>
            ))}
          </Listbox>
        )}

        {/* Payment Method filter */}
        <Select
          value={transactionFilters.type}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setTransactionFilters({
              ...transactionFilters,
              type: e.target.value as '' | TransactionType,
            })
          }
        >
          <option value="">All Payment Methods</option>
          {Object.values(TransactionType).map((type, index) => (
            <option
              key={`${type}-${index}`}
              value={type}
            >
              {getTransactionTypeText(type)}
            </option>
          ))}
        </Select>
      </div>
    </div>
  )
}

export default TransactionFilters
